"use client";

import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AttributeList } from "./attribute-list";
import { InfoHint } from "./info-hint";
import type { AttributeRow } from "./use-category-drafts";
import type { LanguageCode } from "@/constants/languages";
import { newRowUid } from "./use-category-drafts";

interface AttributeSectionProps {
  rows: AttributeRow[];
  isGeneral: boolean;
  hasOwnAttributes: boolean;
  /** True once the user has chosen to diverge from the inherited set. */
  overriding: boolean;
  inheritedFrom: string | null;
  readOnly: boolean;
  languages: LanguageCode[];
  usedKeys: ReadonlySet<string>;
  onChange: (rows: AttributeRow[]) => void;
  onCustomise: () => void;
  onReset: () => void;
}

/**
 * The attribute list plus the sentence explaining where the attributes come
 * from. Inherited attributes render read-only, so an override is never created
 * by accident — only by pressing "Collect different attributes here".
 */
export function AttributeSection({
  rows,
  isGeneral,
  hasOwnAttributes,
  overriding,
  inheritedFrom,
  readOnly,
  languages,
  usedKeys,
  onChange,
  onCustomise,
  onReset,
}: AttributeSectionProps) {
  const locked = !isGeneral && !hasOwnAttributes && !overriding;

  return (
    <section className="space-y-3">
      {!isGeneral && (
        <div
          className={
            hasOwnAttributes
              ? "flex flex-wrap items-center gap-3 rounded-md border bg-muted/30 px-3 py-2 text-[12.5px] text-muted-foreground"
              : "flex flex-wrap items-center gap-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-[12.5px]"
          }
        >
          {hasOwnAttributes ? (
            <>
              <span>These attributes are set just for this category.</span>
              {!readOnly && (
                <button
                  type="button"
                  className="ml-auto font-medium text-pink-700 underline underline-offset-2 hover:text-foreground"
                  onClick={onReset}
                >
                  Go back to {inheritedFrom}
                </button>
              )}
            </>
          ) : (
            <>
              <span>
                Collecting the same <strong>{rows.length} attributes</strong> as{" "}
                <strong>{inheritedFrom}</strong>.
              </span>
              {!readOnly && (
                <Button
                  variant="outline"
                  size="sm"
                  className="ml-auto h-7"
                  onClick={onCustomise}
                >
                  Collect different attributes here
                </Button>
              )}
            </>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          Product attributes
          <InfoHint>
            Youzu fills these in automatically for every product filed in this category.
            The note beside each one tells Youzu what to look for, so write it the way
            you would explain it to a new colleague.
          </InfoHint>
        </h3>
        <Button
          variant="outline"
          size="sm"
          className="h-7"
          disabled={readOnly || locked}
          onClick={() =>
            onChange([
              { uid: newRowUid(), key: "", label: "", description: "", valueType: "string", choices: [], isNew: true },
              ...rows,
            ])
          }
        >
          <Plus className="mr-1 h-3.5 w-3.5" /> Add attribute
        </Button>
      </div>

      <AttributeList
        rows={rows}
        readOnly={readOnly || locked}
        languages={languages}
        usedKeys={usedKeys}
        onChange={onChange}
      />
    </section>
  );
}
